import React from 'react';
import { Box, Button, Typography, Container } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { colors } from '../theme/tokens';
import { logError } from '../services/errorReporting';

/**
 * Error Boundary component to catch React errors and prevent app crashes
 * Implements WCAG-compliant error UI with clear recovery options
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Send error to error reporting service
    logError(error, errorInfo, {
      errorCount: this.state.errorCount + 1,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f7fa',
            padding: 3
          }}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <Container maxWidth="md">
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: 4,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                border: '2px solid #ef4444',
                textAlign: 'center'
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 64,
                  color: '#ef4444',
                  marginBottom: 2
                }}
                aria-hidden="true"
              />

              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: 2
                }}
              >
                {this.state.errorCount > 2
                  ? 'משהו השתבש שוב'
                  : 'אופס! משהו השתבש'}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: colors.text.secondary,
                  marginBottom: 3,
                  fontSize: '16px'
                }}
              >
                {this.state.errorCount > 2
                  ? 'נראה שיש בעיה חוזרת. אנא צור קשר עם התמיכה.'
                  : 'התרחשה שגיאה בלתי צפויה. אנחנו עובדים על זה.'}
              </Typography>

              {/* Action buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginBottom: 3
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                  sx={{
                    backgroundColor: colors.primary.main,
                    '&:hover': {
                      backgroundColor: colors.primary.dark
                    }
                  }}
                  aria-label="טען מחדש את הדף"
                >
                  טען מחדש את הדף
                </Button>

                {this.state.errorCount <= 2 && (
                  <Button
                    variant="outlined"
                    onClick={this.handleReset}
                    sx={{
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                      '&:hover': {
                        borderColor: colors.primary.dark,
                        backgroundColor: 'rgba(19, 36, 161, 0.04)'
                      }
                    }}
                    aria-label="נסה שוב ללא טעינה מחדש"
                  >
                    נסה שוב
                  </Button>
                )}
              </Box>

              {/* Contact support link */}
              <Typography variant="body2" sx={{ color: colors.text.muted }}>
                צריך עזרה?{' '}
                <a
                  href="mailto:support@varno.io"
                  style={{
                    color: colors.primary.main,
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                >
                  צור קשר עם התמיכה
                </a>
              </Typography>

              {/* Development mode error details */}
              {isDevelopment && this.state.error && (
                <Box
                  sx={{
                    marginTop: 4,
                    padding: 3,
                    backgroundColor: '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid #fecaca',
                    textAlign: 'left',
                    direction: 'ltr'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#dc2626',
                      marginBottom: 1
                    }}
                  >
                    🔧 Development Error Details:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: '#991b1b',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      marginBottom: 2
                    }}
                  >
                    {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#7f1d1d',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
